import { get } from "lodash";
import { store } from "@/store/store";
import { NavigationGuardNext, RouteLocationNormalizedGeneric } from "vue-router";
import { Offer_type } from "../types/offer.types";
import { ProfileConfigurationPath } from "../../../backend/src/common/profile/profile";

/**
 * Defines valid permissions for offer-based access control.
 * This ensures that only predefined offer-based permissions can be used.
 */
export type OfferPermission =
    | "isAllowedToBudget"
    | "isAllowedToAuditReport"
    | "isAllowedToFixedAsset"
    | "isAllowedToDashboardAccounting"
    | "isAllowedToOpenPoint"
    | "isAllowedToRestitution"
    | "isAllowedToForecastManagement"
    | "isAllowedToInvoice"
    | "isAllowedToStock"
    | "isAllowedToExpenseReport"
    | "isAllowedToTaxDeclaration"
    | "isAllowedToAccounting"
    | "isAllowedToDashboard"
    | "isAllowedToBundle"
    | "isAllowedToProduction"
    | "isAllowedToDeclaration"
    | "isAllowedToStatusOfFiles"
    | "isAllowedToConnector";

/**
 * Validates whether the user has the required profile permission.
 *
 * @param permission - A specific permission to check (e.g., "user.read", "salesInvoice.write").
 * @returns A Vue Router navigation guard function.
 */
export const checkProfilePermission = (
    permission: ProfileConfigurationPath | ProfileConfigurationPath[],
) => {
    return (
        _to: RouteLocationNormalizedGeneric,
        _from: RouteLocationNormalizedGeneric,
        next: NavigationGuardNext,
    ) => {
        const user = store.state.user;
        const profile = user?.profile?.configuration;

        if (!profile) {
            return next({ name: "403" });
        }

        let hasPermission: boolean;
        if (Array.isArray(permission)) {
            hasPermission = permission.every((perm) => get<boolean>(profile, perm, false));
        } else {
            hasPermission = get<boolean>(profile, permission, false);
        }

        if (!hasPermission) {
            return next({ name: "403" });
        }
        return next();
    };
};

/**
 * Validates whether the user has the required offer-based permission.
 *
 * @param permission - The specific offer-based permission to check (e.g., "isAllowedToInvoice").
 * @returns A Vue Router navigation guard function.
 */
export const checkOfferPermission = (permission: OfferPermission | OfferPermission[]) => {
    return (
        _to: RouteLocationNormalizedGeneric,
        _from: RouteLocationNormalizedGeneric,
        next: NavigationGuardNext,
    ) => {
        const offerType = store?.state?.offerType;

        // Mapping of permissions to allowed offer types
        const isAllowedToMap: Record<OfferPermission, Offer_type[]> = {
            isAllowedToDashboard: [
                Offer_type.ACCOUNTING_PREMIUM,
                Offer_type.BUSINESS_BASIC,
                Offer_type.BUSINESS_ESSENTIAL,
                Offer_type.BUSINESS_PREMIUM,
                Offer_type.TRAINING,
            ],
            isAllowedToBudget: [
                Offer_type.ACCOUNTING_PREMIUM,
                Offer_type.BUSINESS_BASIC,
                Offer_type.BUSINESS_ESSENTIAL,
                Offer_type.BUSINESS_PREMIUM,
                Offer_type.TRAINING,
            ],
            isAllowedToAuditReport: [
                Offer_type.ACCOUNTING_BASIC,
                Offer_type.ACCOUNTING_ESSENTIAL,
                Offer_type.ACCOUNTING_PREMIUM,
                Offer_type.ACCOUNTING_STARTER,
                Offer_type.BUSINESS_PREMIUM,
                Offer_type.TRAINING,
            ],
            isAllowedToFixedAsset: [
                Offer_type.ACCOUNTING_BASIC,
                Offer_type.ACCOUNTING_ESSENTIAL,
                Offer_type.ACCOUNTING_PREMIUM,
                Offer_type.ACCOUNTING_STARTER,
                Offer_type.BUSINESS_PREMIUM,
                Offer_type.BUSINESS_BASIC,
                Offer_type.BUSINESS_ESSENTIAL,
                Offer_type.TRAINING,
            ],
            isAllowedToDashboardAccounting: [
                Offer_type.ACCOUNTING_BASIC,
                Offer_type.ACCOUNTING_ESSENTIAL,
                Offer_type.ACCOUNTING_PREMIUM,
                Offer_type.ACCOUNTING_STARTER,
                Offer_type.BUSINESS_PREMIUM,
                Offer_type.BUSINESS_BASIC,
                Offer_type.BUSINESS_ESSENTIAL,
                Offer_type.TRAINING,
            ],
            isAllowedToOpenPoint: [
                Offer_type.BUSINESS_PREMIUM,
                Offer_type.ACCOUNTING_BASIC,
                Offer_type.ACCOUNTING_ESSENTIAL,
                Offer_type.ACCOUNTING_PREMIUM,
                Offer_type.ACCOUNTING_STARTER,
                Offer_type.TRAINING,
            ],
            isAllowedToRestitution: [
                Offer_type.ACCOUNTING_BASIC,
                Offer_type.ACCOUNTING_ESSENTIAL,
                Offer_type.ACCOUNTING_PREMIUM,
                Offer_type.BUSINESS_BASIC,
                Offer_type.BUSINESS_ESSENTIAL,
                Offer_type.BUSINESS_PREMIUM,
                Offer_type.TRAINING,
            ],
            isAllowedToForecastManagement: [
                Offer_type.ACCOUNTING_ESSENTIAL,
                Offer_type.ACCOUNTING_PREMIUM,
                Offer_type.BUSINESS_ESSENTIAL,
                Offer_type.BUSINESS_PREMIUM,
                Offer_type.TRAINING,
            ],
            isAllowedToInvoice: [
                Offer_type.ACCOUNTING_ESSENTIAL,
                Offer_type.ACCOUNTING_PREMIUM,
                Offer_type.BUSINESS_BASIC,
                Offer_type.BUSINESS_ESSENTIAL,
                Offer_type.BUSINESS_PREMIUM,
                Offer_type.TRAINING,
            ],
            isAllowedToStock: [
                Offer_type.ACCOUNTING_PREMIUM,
                Offer_type.BUSINESS_ESSENTIAL,
                Offer_type.BUSINESS_PREMIUM,
                Offer_type.TRAINING,
            ],
            isAllowedToExpenseReport: [
                Offer_type.ACCOUNTING_STARTER,
                Offer_type.ACCOUNTING_ESSENTIAL,
                Offer_type.ACCOUNTING_PREMIUM,
                Offer_type.BUSINESS_ESSENTIAL,
                Offer_type.BUSINESS_PREMIUM,
                Offer_type.TRAINING,
            ],
            isAllowedToTaxDeclaration: [
                Offer_type.ACCOUNTING_BASIC,
                Offer_type.ACCOUNTING_ESSENTIAL,
                Offer_type.ACCOUNTING_PREMIUM,
                Offer_type.BUSINESS_ESSENTIAL,
                Offer_type.BUSINESS_PREMIUM,
                Offer_type.TRAINING,
            ],
            isAllowedToAccounting: [
                Offer_type.ACCOUNTING_BASIC,
                Offer_type.ACCOUNTING_ESSENTIAL,
                Offer_type.ACCOUNTING_PREMIUM,
                Offer_type.BUSINESS_BASIC,
                Offer_type.BUSINESS_ESSENTIAL,
                Offer_type.BUSINESS_PREMIUM,
                Offer_type.TRAINING,
            ],
            isAllowedToBundle: [
                Offer_type.ACCOUNTING_BASIC,
                Offer_type.ACCOUNTING_ESSENTIAL,
                Offer_type.ACCOUNTING_PREMIUM,
                Offer_type.ACCOUNTING_STARTER,
                Offer_type.BUSINESS_PREMIUM,
                Offer_type.TRAINING,
            ],
            isAllowedToProduction: [
                Offer_type.ACCOUNTING_BASIC,
                Offer_type.ACCOUNTING_ESSENTIAL,
                Offer_type.ACCOUNTING_PREMIUM,
                Offer_type.BUSINESS_PREMIUM,
                Offer_type.TRAINING,
            ],
            isAllowedToDeclaration: [
                Offer_type.ACCOUNTING_BASIC,
                Offer_type.ACCOUNTING_ESSENTIAL,
                Offer_type.ACCOUNTING_PREMIUM,
                Offer_type.BUSINESS_PREMIUM,
                Offer_type.TRAINING,
            ],
            isAllowedToStatusOfFiles: [
                Offer_type.ACCOUNTING_BASIC,
                Offer_type.ACCOUNTING_ESSENTIAL,
                Offer_type.ACCOUNTING_PREMIUM,
                Offer_type.BUSINESS_PREMIUM,
                Offer_type.BUSINESS_ESSENTIAL,
                Offer_type.TRAINING,
            ],
            isAllowedToConnector: [
                Offer_type.ACCOUNTING_ESSENTIAL,
                Offer_type.ACCOUNTING_PREMIUM,
                Offer_type.BUSINESS_ESSENTIAL,
                Offer_type.BUSINESS_PREMIUM,
                Offer_type.TRAINING,
            ],
        };

        if (!offerType) {
            return next({ name: "403" });
        }

        let hasPermission: boolean;
        if (Array.isArray(permission)) {
            hasPermission = permission.every((perm) => isAllowedToMap[perm]?.includes(offerType));
        } else {
            hasPermission = isAllowedToMap[permission]?.includes(offerType);
        }

        if (!hasPermission) {
            return next({ name: "403" });
        }
        return next();
    };
};
